/* eslint-disable react-hooks/exhaustive-deps */
import type { PropsWithChildren } from "react";
import type { Socket } from "socket.io-client";
import type { ISocketContext, IMessage } from "./interfaces";
import { useAuth } from "~contexts/Auth";
import { useStreamContext } from "~contexts/Stream";
import { socketBuilder } from "~services/socket";
import {
  getRoomsWithOperatorsLastMessage,
  groupMessagesByUser,
  orderMessages,
} from "~helpers/chat";
import { createContext, useContext, useState, useEffect, useCallback } from "react";

const SocketContext = createContext<ISocketContext>({
  socket: null,
  setSocket: () => null,
  isLive: false,
  setIsLive: () => false,
  isReaction: false,
  setIsReaction: () => false,
  userCount: 0,
  setUserCount: () => 0,
  messages: [],
  setMessages: () => [],
  messagesRooms: [],
  setMessagesRooms: () => [],
  getChatHistory: () => [],
  updateStreamSocket: () => [],
});

export const SocketContextProvider = ({ children }: PropsWithChildren) => {
  const { signed } = useAuth();
  const { currentStream } = useStreamContext();
  const [isLive, setIsLive] = useState<boolean>(false);
  const [userCount, setUserCount] = useState<number>(0);
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isReaction, setIsReaction] = useState<boolean>(false);
  const [messagesRooms, setMessagesRooms] = useState(new Map());

  const setOrderedMessages = useCallback(
    (messagesArray: IMessage[]) => {
      const orderedMessages = orderMessages(messagesArray);
      setMessages(orderedMessages);
      setMessagesRooms(groupMessagesByUser(orderedMessages));
    },
    [setMessages, setMessagesRooms]
  );

  const getChatHistory = (name: string) => {
    if (!currentStream) return Promise.resolve();
    
    return new Promise((resolve) => {
      socket?.emit(
        "getChatHistory",
        `${currentStream.brand}-${currentStream.name}-${process.env.REACT_APP_ENVIRONMENT}-${name}-chat-adm`,
        resolve
      );
    });
  };

  const updateStreamSocket: ISocketContext["updateStreamSocket"] = (data = {}) => {
    const payload: Record<string, unknown> = {};

    if (typeof data.is_live !== "undefined") payload.isLive = data.is_live;
    if (typeof data.has_reaction !== "undefined") payload.isReaction = data.has_reaction;
    if (typeof data.has_chat !== "undefined") payload.isChat = data.has_chat;
    if (typeof data.themeUpdated !== "undefined") payload.themeUpdated = data.themeUpdated;
    if (typeof data.categoriesUpdated !== "undefined")
      payload.categoriesUpdated = data.categoriesUpdated;
    if (typeof data.productsUpdated !== "undefined")
      payload.productsUpdated = data.productsUpdated;
    if (typeof data.productAdded !== "undefined") payload.productAdded = data.productAdded;
    if (typeof data.productRemoved !== "undefined") payload.productRemoved = data.productRemoved;

    if (
      typeof data.video_url !== "undefined" ||
      typeof data.vimeo_id !== "undefined" ||
      typeof data.is_rerun !== "undefined" ||
      typeof data.rerun_start_time !== "undefined"
    ) {
      payload.video = {
        url: data.video_url,
        id: data.vimeo_id,
        isRerun: data.is_rerun,
        rerunStartTime: data.rerun_start_time,
      };
    }

    socket?.emit("streamUpdated", payload);
  };

  useEffect(() => {
    if (signed && currentStream) {
      setSocket(socketBuilder(currentStream.brand, currentStream.name));
    }
  }, [signed, currentStream]);

  useEffect(() => {
    if (!socket) return undefined;

    socket.on("connect", () => {
      socket.emit("getCurrentProduct");
    });

    socket.on("currentData", (initialData) => {
      setOrderedMessages(initialData.messages);
      setUserCount(initialData.userCount);
    });

    socket.on("receivedMessage", (receivedMessage) => {
      setMessages((msgArr) => [...msgArr, receivedMessage]);
      setMessagesRooms((currState) =>
        getRoomsWithOperatorsLastMessage(currState, receivedMessage)
      );
    });

    socket.on("refreshStream", (streamData) => {
      setIsLive(streamData.isLive);
    });

    return () => {
      socket.off("connect");
      socket.off("currentData");
      socket.off("receivedMessage");
      socket.off("refreshStream");
    };
  }, [socket, signed]);

  return (
    <SocketContext.Provider
      value={{
        socket,
        setSocket,
        isLive,
        setIsLive,
        isReaction,
        setIsReaction,
        userCount,
        setUserCount,
        messages,
        setMessages,
        messagesRooms,
        setMessagesRooms,
        getChatHistory,
        updateStreamSocket,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocketContext = () => {
  const context = useContext(SocketContext);

  return context;
};
